import type { JSX } from 'react'
import { checkHealth, useServerStatus } from './api/serverStatus'
import { Toasts } from './components/Toasts'
import { navigate, useRoute } from './lib/router'
import { EditorPage } from './pages/EditorPage'
import { GamesPage } from './pages/GamesPage'
import { LoginPage } from './pages/LoginPage'
import { RoomPage } from './pages/RoomPage'
import { PlayPage } from './pages/PlayPage'
import { ProjectsPage } from './pages/ProjectsPage'

function OfflineBanner(): JSX.Element | null {
  const status = useServerStatus()
  if (status.online === null) return null
  if (status.online === false) {
    return (
      <div className="offline-banner" role="alert">
        <strong>Сервер недоступен.</strong>
        <span>
          {status.lastError ?? 'Нет ответа от бэкенда'}. Запустите его командой{' '}
          <span className="mono">go run ./cmd/server</span> в папке <span className="mono">backend</span>.
        </span>
        <span className="spacer" />
        <button type="button" className="ghost" onClick={() => void checkHealth()}>
          Проверить снова
        </button>
      </div>
    )
  }
  if (!status.ffmpeg) {
    return (
      <div className="offline-banner warn">
        <strong>ffmpeg не найден на сервере.</strong>
        <span>Разметка, запись и превью работают, финальный рендер — нет.</span>
      </div>
    )
  }
  return null
}

export function App(): JSX.Element {
  const route = useRoute()
  const status = useServerStatus()

  return (
    <div className="app">
      <header className="app-header">
        <button type="button" className="logo" onClick={() => navigate({ name: 'games' })}>
          Озвучка
        </button>
        <span className="faint">озвучиваем видео по ролям</span>
        <span className="spacer" />
        {status.online === true ? (
          <span className="badge" style={{ color: 'var(--success)' }}>
            <span className="dot" /> сервер на связи{status.version ? ` · ${status.version}` : ''}
          </span>
        ) : status.online === false ? (
          <span className="badge" style={{ color: 'var(--danger)' }}>
            <span className="dot" /> нет связи
          </span>
        ) : (
          <span className="badge">проверяю сервер…</span>
        )}
        {status.analyzer.available ? (
          <span
            className="badge"
            title={
              status.analyzer.gpu
                ? `Сервис автоматического разбора: ${status.analyzer.gpu}`
                : 'Сервис автоматического разбора на связи'
            }
          >
            разбор{status.analyzer.device ? ` · ${status.analyzer.device}` : ''}
          </span>
        ) : null}
      </header>

      <OfflineBanner />

      <main className="app-body">
        {route.name === 'games' ? (
          <GamesPage />
        ) : route.name === 'room' ? (
          <RoomPage key={route.code} code={route.code} />
        ) : route.name === 'login' ? (
          <LoginPage />
        ) : route.name === 'editor' ? (
          <EditorPage key={route.projectId} projectId={route.projectId} />
        ) : route.name === 'play' ? (
          <PlayPage key={route.projectId} projectId={route.projectId} stage={route.stage} />
        ) : (
          <ProjectsPage />
        )}
      </main>

      <Toasts />
    </div>
  )
}
